import { useEffect } from 'react';

/**
 * Lightweight head-tag manager for SPA SEO without pulling in react-helmet.
 *
 * Sets <title>, common <meta>, <link rel="canonical">, OG / Twitter cards,
 * and (optionally) one JSON-LD <script> per call. On unmount, restores the
 * previous <title> and removes only the tags this hook created.
 *
 * Googlebot fully renders JS, so client-set tags are honored. For social
 * scrapers that don't run JS (Facebook, LinkedIn, Slack) we still get the
 * default OG tags baked into /public/index.html on first-paint of the SPA;
 * blog posts gain richer per-post tags here as a progressive enhancement.
 */
export function useDocumentHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'article',
  twitterCard = 'summary_large_image',
  jsonLd,
  keywords,
} = {}) {
  // Compare JSON content, not the identity of an inline object passed by a page.
  const serializedJsonLd = JSON.stringify(jsonLd || null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const prevTitle = document.title;
    const created = [];

    if (title) {
      document.title = title;
    }

    const setMetaByName = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    };
    const setMetaByProperty = (property, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('href', href);
    };

    setMetaByName('description', description);
    setMetaByName('keywords', keywords);
    setLink('canonical', canonical);

    setMetaByProperty('og:title', ogTitle || title);
    setMetaByProperty('og:description', ogDescription || description);
    setMetaByProperty('og:image', ogImage);
    setMetaByProperty('og:type', ogType);
    setMetaByProperty('og:url', canonical);

    setMetaByName('twitter:card', twitterCard);
    setMetaByName('twitter:title', ogTitle || title);
    setMetaByName('twitter:description', ogDescription || description);
    setMetaByName('twitter:image', ogImage);

    let jsonLdScript = null;
    if (serializedJsonLd && serializedJsonLd !== 'null') {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.text = serializedJsonLd;
      jsonLdScript.dataset.dynamic = 'blog';
      document.head.appendChild(jsonLdScript);
      created.push(jsonLdScript);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [
    title,
    description,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    twitterCard,
    keywords,
    serializedJsonLd,
  ]);
}

export default useDocumentHead;
